import React, { useEffect, useState } from 'react';
import MarketSelector from './MarketSelector';
import MarketService, { MarketInfo, AppMarket } from '../../services/MarketService';
import ContactSalesModal from '../Shared/ContactSalesModal';
import './HubLanding.css';
import './MarketSelector.css';

export interface HubLandingProps {
  onSelectMarket?: (market: MarketInfo) => void;
  overrideGeo?: 'uk' | 'texas';
}

/**
 * Feature grid items — each tagged with a region (uk / us) so the hub
 * advertises the full multi-market compliance stack.  The feat-grid mirrors
 * the hub.html mockup; region tags reuse the same token set as the market cards.
 */
interface FeatItem {
  region: 'uk' | 'us';
  icon: string;
  title: string;
  desc: string;
}

const FEAT_ITEMS: FeatItem[] = [
  { region: 'uk', icon: '🔥', title: 'Gas Safety (CP12)', desc: 'Annual gas safety certificate tracking, engineer management, tenant notification workflows.' },
  { region: 'uk', icon: '⚡', title: 'EPC Management', desc: 'Energy Performance Certificate tracking, rating improvement recommendations, renewal reminders.' },
  { region: 'uk', icon: '👥', title: 'HMO Licensing', desc: 'Selective and additional HMO licensing tracking, local authority requirements, compliance deadlines.' },
  { region: 'uk', icon: '🧾', title: 'Deposit Protection', desc: 'Custodial/insured scheme integration, DPS API, move-in/move-out date tracking.' },
  { region: 'us', icon: '⚖️', title: 'SB 38 Eviction Shield', desc: '3-day notice generator, 10–21 day hearing tracker, CARES Act override, sworn appeal countdown.' },
  { region: 'us', icon: '🔑', title: '7-Day Re-Key Ledger', desc: 'Mandatory re-keying tracker tied to tenant turnover. Avoid the $500+ statutory penalty.' },
  { region: 'us', icon: '🌊', title: 'Flood Disclosure', desc: 'Section 92.0135 compliant. FEMA flood zone integration, 5-year history, Houston lease attachment.' },
  { region: 'us', icon: '🏦', title: 'Trust Account (IARW)', desc: 'TREC-compliant trust/escrow tracking for licensed PMs. Self-managers see simplified finances.' },
  { region: 'us', icon: '🗺️', title: 'Jurisdiction-Aware Engine', desc: 'Houston vs. Dallas vs. suburb — auto-switches requirements across cities.' },
  { region: 'us', icon: '⚖️', title: 'Fair Housing Compliance', desc: 'Houston: source-of-income + veteran. Dallas: sexual orientation + gender identity.' },
  { region: 'us', icon: '📄', title: 'IRS Schedule E + 1099', desc: 'Automated Schedule E mapping, 1099-NEC contractor tracking, rent roll & T12 exports.' },
  { region: 'us', icon: '🛡️', title: 'HOA & STR Management', desc: 'CC&R compliance, Houston STR registration, Dallas STR ban detection, lodging tax tracking.' },
];

const HERO_STATS = [
  { num: '10K+', label: 'Landlords' },
  { num: '21-Day', label: 'UK Eviction / TX Hearing' },
  { num: '$0', label: 'Penalty Risk' },
  { num: 'SB 38', label: 'Texas Compliant' },
];

export const HubLanding: React.FC<HubLandingProps> = ({
  onSelectMarket,
  overrideGeo,
}) => {
  const [detectedGeo, setDetectedGeo] = useState<'uk' | 'texas'>('uk');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    const geo = overrideGeo || MarketService.detectGeoMarket();
    setDetectedGeo(geo);
  }, [overrideGeo]);

  const handleMarketSelect = (market: MarketInfo) => {
    if (onSelectMarket) {
      onSelectMarket(market);
    } else {
      MarketService.redirectToMarket(market.id);
    }
  };

  return (
    <div className="hub-container" data-testid="hub-landing-page">
      {/* Navigation — retained from hub.html mockup, reuse detectGeoMarket + hub brand */}
      <nav className="hub-nav">
        <div className="hub-nav-inner">
          <ul className="hub-nav-links">
            <li><a href="#hero">Home</a></li>
            <li><a href="#markets">Markets</a></li>
            <li><a href="#features">Features</a></li>
            <li><a href="#cta">Compliance</a></li>
          </ul>
        </div>
      </nav>

      <main>
        {/* Hero — region-tagged (UK + Texas), hero-stats row, CTA */}
        <section className="hub-hero" id="hero" data-testid="hub-hero">
          <div className="hub-hero-inner">
            <h1 data-testid="hub-heading">
              Compliance Engine for
              <br />
              <span>Landlords Everywhere</span>
            </h1>
            <p data-testid="hub-subheading" className="hub-subheading">
              UK gas safety, EPCs, HMO licensing — and Texas SB 38 eviction shield,
              Dallas/Houston registration, flood disclosure. One platform, region-specific compliance.
            </p>
            <div className="hero-cta">
              <a href="#markets" className="btn btn-primary btn-lg" data-testid="hero-cta-primary">
                Choose Region <i className="fa-solid fa-arrow-right"></i>
              </a>
            </div>
            <div className="hero-stats" data-testid="hero-stats">
              {HERO_STATS.map((stat, i) => (
                <div className="hero-stat" key={i} data-testid={`hero-stat-${i}`}>
                  <div className="hero-stat-num">{stat.num}</div>
                  <div className="hero-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Market Selector — fully data-driven, SAME localStorage key (selected_jurisdiction) */}
        <section id="markets" data-testid="market-selector-section">
          <div className="market-selector-wrapper">
            <div className="market-selector-label" data-testid="market-selector-label">
              Choose Your Market
            </div>
            <MarketSelector
              detectedMarketId={detectedGeo}
              onMarketSelect={handleMarketSelect}
            />
          </div>
        </section>

        {/* Global feature grid — UK + Texas region cards */}
        <section id="features" className="hub-features-section">
          <div className="hub-sec-inner">
            <span className="section-label" data-testid="features-label">Platform</span>
            <h2 className="section-title" data-testid="features-title">Compliance on Autopilot</h2>
            <p className="section-desc" data-testid="features-desc">
              Every feature maps to a statute. UK and Texas modules run independently — no cross-contamination.
            </p>
            <div className="feat-grid" data-testid="feat-grid">
              {FEAT_ITEMS.map((feat, i) => (
                <div className="feat-card" key={i} data-testid={`feat-card-${i}`}>
                  <span className={`region ${feat.region === 'uk' ? 'uk' : 'us'}`} data-testid={`feat-region-${i}`}>
                    {feat.region === 'uk' ? '🇬🇧 UK' : '🇺🇸 Texas'}
                  </span>
                  <div className="icon" data-testid={`feat-icon-${i}`}>{feat.icon}</div>
                  <h3 data-testid={`feat-title-${i}`}>{feat.title}</h3>
                  <p data-testid={`feat-desc-${i}`}>{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="cta" className="hub-cta-section">
          <div className="hub-sec-inner">
            <h2 className="section-title" data-testid="cta-title">Compliance on Autopilot</h2>
            <p className="section-desc cta-desc-line" data-testid="cta-desc">
              UK or Texas — pick your region, get the right compliance engine, and stop worrying about statutory deadlines.
            </p>
            <div className="hero-cta" style={{ justifyContent: 'center' }}>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setIsContactModalOpen(true); }}
                className="btn btn-outline btn-lg"
                data-testid="cta-secondary"
              >
                Talk to Sales <i className="fa-solid fa-arrow-right"></i>
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="hub-footer" data-testid="hub-footer">
        <p data-testid="hub-footer-text">
          Landlord Pro · Compliance Engine Limited
        </p>
      </footer>

      {isContactModalOpen && (
        <ContactSalesModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
      )}
    </div>
  );
};

export default HubLanding;
